import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import gstVerificationCounterService from '../../services/GstVerificationCounterService.js';
import gstVerificationPaymentService from '../../services/GstVerificationPaymentService.js';
import AddPaymentModal from './payment-modals/AddPaymentModal.js';
import EditPaymentModal from './payment-modals/EditPaymentModal.js';
import ViewPaymentModal from './payment-modals/ViewPaymentModal.js';
import {
  ShieldCheck, ArrowLeft, DollarSign, TrendingDown, Settings,
  Download, Plus, Eye, Edit2, Trash2, CheckCircle, AlertTriangle,
  Calendar, BarChart2, Save, RefreshCw, CreditCard, Mail
} from "lucide-react";
import Loader from '../common/Loader.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useTranslation } from 'react-i18next';


const ViewGstVerificationCounter = () => {
  const { t } = useTranslation();
  const { counterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab') || 'overview';

  const [counter, setCounter] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [activeTab, setActiveTab] = useState(activeTabParam);

  // Modals
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [openPaymentActionId, setOpenPaymentActionId] = useState(null);
  const paymentActionRef = useRef(null);
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  // Settings inline edit
  const [costInput, setCostInput] = useState('');
  const [savingCost, setSavingCost] = useState(false);
  const [editingCost, setEditingCost] = useState(false);

  const navigate = useNavigate();

  const fetchCounter = useCallback(async () => {
    try {
      const response = await gstVerificationCounterService.getByCompany(counterId);
      setCounter(response.data);
      setCostInput(response.data.cost_per_search || '');
    } catch (err) {
      Alert.error('Failed to load counter details');
      navigate('/company-gst-verification-counters');
    } finally {
      setLoading(false);
    }
  }, [counterId, navigate]);

  const fetchPayments = useCallback(async () => {
    try {
      setPaymentsLoading(true);
      const response = await gstVerificationPaymentService.getPaymentsByCompany(counterId);
      setPayments(response.data || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setPaymentsLoading(false);
    }
  }, [counterId]);

  useEffect(() => {
    fetchCounter();
    fetchPayments();
  }, [fetchCounter, fetchPayments]);

  useEffect(() => {
    if (!openPaymentActionId) return undefined;
    const handleClickOutside = (event) => {
      if (paymentActionRef.current && !paymentActionRef.current.contains(event.target)) {
        setOpenPaymentActionId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPaymentActionId]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleSaveCost = async () => {
    if (!costInput || parseFloat(costInput) < 0) {
      Alert.error('Please enter a valid cost per search');
      return;
    }
    setSavingCost(true);
    try {
      await gstVerificationCounterService.updateCostPerMessage(counterId, parseFloat(costInput));
      Alert.success('Cost per search updated successfully!');
      setEditingCost(false);
      fetchCounter();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update cost');
    } finally {
      setSavingCost(false);
    }
  };

  const handleResetCounter = async () => {
    if (!window.confirm('Reset the search counter? This cannot be undone.')) return;
    try {
      await gstVerificationCounterService.resetCounter(counterId, false);
      Alert.success('Counter reset successfully!');
      fetchCounter();
      fetchPayments();
    } catch (err) {
      Alert.error('Failed to reset counter');
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const blob = await gstVerificationCounterService.downloadInvoice(counterId, true);
      await downloadBlob(
        blob,
        `gst_verification_invoice_${counterId}_${new Date().toISOString().split('T')[0]}.pdf`,
        'application/pdf',
      );
      Alert.success('Invoice downloaded!');
    } catch (err) {
      Alert.error('Failed to download invoice');
    }
  };

  const handleEmailInvoice = async () => {
    if (emailing) return;
    const companyEmail = counter?.company_email || counter?.company?.email || counter?.email;
    const confirmed = await Alert.confirm(
      companyEmail
        ? `Send GST verification invoice to ${companyEmail}?`
        : 'Send GST verification invoice to the company email?',
      'Email Invoice'
    );
    if (!confirmed) return;
    try {
      setEmailing(true);
      const res = await gstVerificationCounterService.sendInvoiceEmail(counterId, true);
      Alert.success(res.message || 'Invoice emailed successfully!');
    } catch (err) {
      Alert.error(err.response?.data?.error || err.response?.data?.message || 'Failed to email invoice');
    } finally {
      setEmailing(false);
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const blob = await gstVerificationPaymentService.downloadReceipt(paymentId);
      await downloadBlob(blob, `receipt_${paymentId}.pdf`, 'application/pdf');
    } catch (err) {
      Alert.error('Failed to download receipt');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment? This cannot be undone.')) return;
    try {
      await gstVerificationPaymentService.deletePayment(paymentId);
      Alert.success('Payment deleted!');
      fetchCounter();
      fetchPayments();
    } catch (err) {
      Alert.error('Failed to delete payment');
    }
  };

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', {
      style: 'currency', currency: 'INR',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const companyName = counter ? (counter.company_name || counter.company?.company_name || 'N/A') : '';
  const dueAmount = counter ? parseFloat(counter.due_amount || 0) : 0;
  const isPaid = dueAmount <= 0;
  const paymentPct = counter ? parseFloat(counter.payment_percentage || 0) : 0;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'payments', label: 'Payments', icon: CreditCard, count: payments.length },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <DashboardLayout>
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading counter details..." />
        </div>
      ) : !counter ? null : (
        <>
              {/* Page Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" onClick={() => navigate('/company-gst-verification-counters')} icon={ArrowLeft} />
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl font-bold text-gray-900">{companyName}</h1>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${isPaid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {isPaid ? '✓ Paid Up' : `Due: ${formatCurrency(dueAmount)}`}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">GST Verification Counter Details</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button variant="secondary" onClick={handleDownloadInvoice} icon={Download} disabled={emailing}>
                    Download Invoice
                  </Button>
                  <Button variant="secondary" onClick={handleEmailInvoice} icon={Mail} disabled={emailing}>
                    {emailing ? 'Emailing...' : 'Email Invoice'}
                  </Button>
                </div>
              </div>

              {/* Stat Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Searches */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Searches</p>
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ShieldCheck className="w-4 h-4 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{(counter.search_count || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {counter.last_search_at ? `Last: ${formatDate(counter.last_search_at)}` : 'No searches yet'}
                  </p>
                </div>

                {/* Cost/Search */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost/Search</p>
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="w-4 h-4 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(counter.cost_per_search || 0)}</p>
                  <p className="text-xs text-gray-400 mt-1">Per GST verification search</p>
                </div>

                {/* Total Cost */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Cost</p>
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <BarChart2 className="w-4 h-4 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(counter.total_cost || 0)}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Paid: {formatCurrency(counter.paid_amount || 0)}
                  </p>
                </div>

                {/* Balance Due */}
                <div className={`rounded-xl border p-4 ${isPaid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className={`text-xs font-semibold uppercase tracking-wide ${isPaid ? 'text-green-600' : 'text-red-600'}`}>Balance Due</p>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isPaid ? 'bg-green-100' : 'bg-red-100'}`}>
                      {isPaid
                        ? <CheckCircle className="w-4 h-4 text-green-600" />
                        : <AlertTriangle className="w-4 h-4 text-red-600" />}
                    </div>
                  </div>
                  <p className={`text-2xl font-bold ${isPaid ? 'text-green-700' : 'text-red-700'}`}>
                    {isPaid ? '₹0.00' : formatCurrency(dueAmount)}
                  </p>
                  <p className={`text-xs mt-1 ${isPaid ? 'text-green-600' : 'text-red-500'}`}>
                    {isPaid ? 'Fully settled' : `${paymentPct.toFixed(1)}% paid`}
                  </p>
                </div>
              </div>

              {/* Tabs + Content */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

                {/* Tab Bar */}
                <div className="flex border-b border-gray-200 bg-gray-50">
                  {tabs.map(({ id, label, icon: Icon, count }) => (
                    <button
                      key={id}
                      onClick={() => handleTabChange(id)}
                      className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                        activeTab === id
                          ? 'border-blue-500 text-blue-600 bg-white'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                      {count !== undefined && count > 0 && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                          activeTab === id ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* ─── Overview Tab ─── */}
                {activeTab === 'overview' && (
                  <div className="p-5 sm:p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">Billing Summary</h3>

                    <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-5">
                      {/* Usage Breakdown */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Usage Breakdown</h4>
                        <div className="space-y-3">
                          {[
                            { label: 'Total Searches', value: (counter.search_count || 0).toLocaleString() },
                            { label: 'Cost per Search', value: formatCurrency(counter.cost_per_search || 0) },
                            { label: 'Gross Total Cost', value: formatCurrency(counter.total_cost || 0) },
                          ].map(({ label, value }) => (
                            <div key={label} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{label}</span>
                              <span className="font-semibold text-gray-900">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Payment Summary */}
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Payment Summary</h4>
                        <div className="space-y-3">
                          {[
                            { label: 'Amount Paid', value: formatCurrency(counter.paid_amount || 0), cls: 'text-green-600' },
                            { label: 'Balance Due', value: formatCurrency(dueAmount), cls: isPaid ? 'text-green-600' : 'text-red-600' },
                            { label: 'Payment Status', value: isPaid ? 'Fully Paid' : 'Outstanding', cls: isPaid ? 'text-green-600' : 'text-red-600' },
                          ].map(({ label, value, cls }) => (
                            <div key={label} className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">{label}</span>
                              <span className={`font-semibold ${cls}`}>{value}</span>
                            </div>
                          ))}
                        </div>

                        {/* Progress bar */}
                        {parseFloat(counter.total_cost || 0) > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between text-xs text-gray-500 mb-1">
                              <span>Payment Progress</span>
                              <span>{paymentPct.toFixed(1)}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${paymentPct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min(paymentPct, 100)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timestamps */}
                    <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                      {[
                        { icon: Calendar, label: 'Counter Created', value: formatDate(counter.created_at) },
                        { icon: ShieldCheck, label: 'Last Message', value: formatDate(counter.last_search_at) },
                        { icon: DollarSign, label: 'Last Payment', value: formatDate(counter.last_payment_date) },
                      ].map(({ icon: Icon, label, value }) => (
                        <div key={label} className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-gray-500" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{label}</p>
                            <p className="text-sm font-medium text-gray-900">{value}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Quick action if due */}
                    {!isPaid && (
                      <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-800">Payment Required</p>
                            <p className="text-xs text-amber-600">Outstanding balance of {formatCurrency(dueAmount)}</p>
                          </div>
                        </div>
                        <Button variant="primary" onClick={() => handleTabChange('payments')}>
                          Add Payment
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* ─── Payments Tab ─── */}
                {activeTab === 'payments' && (
                  <>
                    <div className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">Payment History</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{payments.length} transaction{payments.length !== 1 ? 's' : ''} recorded</p>
                      </div>
                      <button
                        onClick={() => setShowAddPaymentModal(true)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Add Payment
                      </button>
                    </div>

                    {paymentsLoading ? (
                      <div className="p-10 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                        <p className="text-gray-500 text-sm">Loading payments...</p>
                      </div>
                    ) : payments.length === 0 ? (
                      <div className="p-12 text-center">
                        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <DollarSign className="w-7 h-7 text-gray-300" />
                        </div>
                        <p className="text-gray-500 font-medium">No payments recorded yet</p>
                        <Button variant="link" onClick={() => setShowAddPaymentModal(true)} className="mt-3">
                          + Record first payment
                        </Button>
                      </div>
                    ) : (
                      <>
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase w-8">Sr</th>
                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Payment ID</th>
                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Method</th>
                              <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Status</th>
                              <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {payments.map((payment, idx) => (
                              <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-3.5 text-sm text-gray-400">{idx + 1}</td>
                                <td className="px-5 py-3.5">
                                  <span className="text-sm font-mono font-semibold text-gray-800">{payment.payment_id || payment.id}</span>
                                </td>
                                <td className="px-5 py-3.5 text-sm text-gray-600">{formatDate(payment.payment_date)}</td>
                                <td className="px-5 py-3.5 text-right text-sm font-bold text-gray-900">{formatCurrency(payment.amount)}</td>
                                <td className="px-5 py-3.5 text-sm text-gray-600">
                                  {gstVerificationPaymentService.getPaymentMethodDisplayName(payment.payment_method)}
                                </td>
                                <td className="px-5 py-3.5 text-center">
                                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
                                    payment.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                                    payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {gstVerificationPaymentService.getPaymentStatusDisplayName(payment.payment_status)}
                                  </span>
                                </td>
                                <td className="px-5 py-3.5">
                                  <div className="flex justify-center">
                                    <RowActionsMenu
                                      isOpen={openPaymentActionId === payment.id}
                                      onToggle={() =>
                                        setOpenPaymentActionId(
                                          openPaymentActionId === payment.id ? null : payment.id
                                        )
                                      }
                                      onClose={() => setOpenPaymentActionId(null)}
                                      menuRef={paymentActionRef}
                                      onView={() => {
                                        setSelectedPaymentId(payment.id);
                                        setShowViewPaymentModal(true);
                                      }}
                                      onEdit={() => {
                                        setSelectedPaymentId(payment.id);
                                        setShowEditPaymentModal(true);
                                      }}
                                      onDelete={() => handleDeletePayment(payment.id)}
                                      extraItems={
                                        payment.has_receipt
                                          ? [
                                              {
                                                label: 'Download Receipt',
                                                icon: '📄',
                                                onClick: () => handleDownloadReceipt(payment.id),
                                              },
                                            ]
                                          : []
                                      }
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="md:hidden p-3 space-y-3">
                        {payments.map((payment, idx) => (
                          <div
                            key={payment.id}
                            className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                  {payment.payment_id || payment.id}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">{formatDate(payment.payment_date)}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  payment.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                                  payment.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {gstVerificationPaymentService.getPaymentStatusDisplayName(payment.payment_status)}
                                </span>
                                <RowActionsMenu
                                  isOpen={openPaymentActionId === payment.id}
                                  onToggle={() =>
                                    setOpenPaymentActionId(
                                      openPaymentActionId === payment.id ? null : payment.id
                                    )
                                  }
                                  onClose={() => setOpenPaymentActionId(null)}
                                  menuRef={paymentActionRef}
                                  onView={() => {
                                    setSelectedPaymentId(payment.id);
                                    setShowViewPaymentModal(true);
                                  }}
                                  onEdit={() => {
                                    setSelectedPaymentId(payment.id);
                                    setShowEditPaymentModal(true);
                                  }}
                                  onDelete={() => handleDeletePayment(payment.id)}
                                  extraItems={
                                    payment.has_receipt
                                      ? [
                                          {
                                            label: 'Download Receipt',
                                            icon: '📄',
                                            onClick: () => handleDownloadReceipt(payment.id),
                                          },
                                        ]
                                      : []
                                  }
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Amount:</span>
                                <p className="font-bold text-gray-900 mt-0.5">{formatCurrency(payment.amount)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Method:</span>
                                <p className="font-medium text-gray-900 mt-0.5">
                                  {gstVerificationPaymentService.getPaymentMethodDisplayName(payment.payment_method)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Sr No:</span>
                                <p className="font-medium text-gray-600 mt-0.5">{idx + 1}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      </>
                    )}
                  </>
                )}

                {/* ─── Settings Tab ─── */}
                {activeTab === 'settings' && (
                  <div className="p-5 sm:p-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">Cost Per Search Settings</h3>
                    <p className="text-sm text-gray-500 mb-6">Update the billing rate applied to GST verification searches.</p>

                    <div className="max-w-md">
                      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Cost per Message (₹)</label>

                        {editingCost ? (
                          <div className="space-y-3">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={costInput}
                              onChange={e => setCostInput(e.target.value)}
                              className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                              placeholder="0.00"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <Button variant="primary" onClick={handleSaveCost} loading={savingCost} icon={Save} className="flex-1">
                                {savingCost ? 'Saving...' : 'Save Changes'}
                              </Button>
                              <Button variant="secondary" onClick={() => { setEditingCost(false); setCostInput(counter.cost_per_search || ''); }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <div className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-900">
                              {formatCurrency(counter.cost_per_search || 0)}
                            </div>
                            <Button variant="primary" onClick={() => setEditingCost(true)} icon={Edit2}>
                              Edit
                            </Button>
                          </div>
                        )}

                        <p className="text-xs text-gray-400 mt-3">
                          Changing this rate recalculates total cost and due amount for all searches.
                        </p>
                      </div>

                      {/* Danger zone */}
                      <div className="mt-5 bg-red-50 rounded-xl border border-red-200 p-5">
                        <h4 className="text-sm font-semibold text-red-700 mb-1">Danger Zone</h4>
                        <p className="text-xs text-red-500 mb-3">Reset the search counter back to zero. This action cannot be undone.</p>
                        <Button variant="danger" onClick={handleResetCounter} icon={RefreshCw}>
                          Reset Counter
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
      {/* Modals */}
      {showAddPaymentModal && (
        <AddPaymentModal
          companyId={counterId}
          dueAmount={dueAmount}
          onClose={() => setShowAddPaymentModal(false)}
          onSuccess={() => { setShowAddPaymentModal(false); fetchCounter(); fetchPayments(); }}
        />
      )}
      {showViewPaymentModal && selectedPaymentId && (
        <ViewPaymentModal
          paymentId={selectedPaymentId}
          onClose={() => { setShowViewPaymentModal(false); setSelectedPaymentId(null); }}
          onEdit={() => { setShowViewPaymentModal(false); setShowEditPaymentModal(true); }}
        />
      )}
      {showEditPaymentModal && selectedPaymentId && (
        <EditPaymentModal
          paymentId={selectedPaymentId}
          dueAmount={dueAmount}
          onClose={() => { setShowEditPaymentModal(false); setSelectedPaymentId(null); }}
          onSuccess={() => { setShowEditPaymentModal(false); setSelectedPaymentId(null); fetchCounter(); fetchPayments(); }}
        />
      )}
        </>
      )}
    </DashboardLayout>
  );
};

export default ViewGstVerificationCounter;